/**
 * IMM HAWAII PARTS — Custom Mega Menu JS (v2.1)
 * FILE: assets/custom-mega-menu.js
 *
 * Behavior:
 * ─────────────────────────────────────────────────────────────
 * • Hover any top-level nav button → opens its mega panel
 * • Moving mouse to sidebar tab   → switches right-side content
 * • Mouse leaves entire nav area  → panel closes (with delay)
 * • Click trigger when closed     → opens panel (no navigation)
 * • Click trigger when open       → navigates to the link
 * • Escape key                    → closes panel
 * • Panel top is auto-calculated from header bottom
 * ─────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  /* ── Config ───────────────────────────────────────────────── */
  const OPEN_DELAY  = 80;   // ms before opening on hover
  const CLOSE_DELAY = 220;  // ms before closing after mouse leaves

  /* ── Elements ─────────────────────────────────────────────── */
  const nav      = document.getElementById('hpm-nav');
  const overlay  = document.getElementById('hpm-overlay');
  const triggers = document.querySelectorAll('[data-mega-trigger]');
  const panels   = document.querySelectorAll('[data-mega-panel]');

  if (!nav || !triggers.length) return;

  /* ── State ────────────────────────────────────────────────── */
  let activePanelId = null;
  let openTimer     = null;
  let closeTimer    = null;

  /* ── Utility ──────────────────────────────────────────────── */
  function getHeaderBottom() {
    const header =
      document.querySelector('sticky-header') ||
      document.querySelector('.header-wrapper') ||
      document.querySelector('header.header') ||
      document.querySelector('header');
    if (!header) return 70;
    return Math.round(header.getBoundingClientRect().bottom);
  }

  function setPanelTop(panel) {
    panel.style.top = getHeaderBottom() + 'px';
  }

  /* ── Open a specific panel ────────────────────────────────── */
  function openPanel(id) {
    clearTimeout(closeTimer);

    if (activePanelId === id) return; // already open

    // Close previous without animation
    if (activePanelId !== null) {
      closePanelDOM(activePanelId);
    }

    activePanelId = id;

    const trigger = document.querySelector(`[data-mega-trigger="${id}"]`);
    const panel   = document.querySelector(`[data-mega-panel="${id}"]`);
    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', 'true');
    setPanelTop(panel);
    panel.removeAttribute('hidden');

    if (overlay) overlay.classList.add('is-on');
    // NOTE: intentionally NOT setting body overflow:hidden — it causes
    // layout shifts that move the header and trigger spurious mouseleave events
  }

  /* ── Close all panels ─────────────────────────────────────── */
  function closeAll() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);

    panels.forEach(p => p.setAttribute('hidden', ''));
    triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));

    if (overlay) overlay.classList.remove('is-on');
    activePanelId = null;
  }

  /* ── Close one panel DOM (no state change) ────────────────── */
  function closePanelDOM(id) {
    const trigger = document.querySelector(`[data-mega-trigger="${id}"]`);
    const panel   = document.querySelector(`[data-mega-panel="${id}"]`);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel)   panel.setAttribute('hidden', '');
  }

  function scheduleOpen(id) {
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    openTimer = setTimeout(() => openPanel(id), OPEN_DELAY);
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    closeTimer = setTimeout(closeAll, CLOSE_DELAY);
  }

  /* ── Nav-level hover ──────────────────────────────────────────
     Moving between nav items or from nav to panel no longer
     starts the close timer — only leaving the entire nav does.
  ─────────────────────────────────────────────────────────────── */
  nav.addEventListener('mouseenter', () => clearTimeout(closeTimer));
  nav.addEventListener('mouseleave', scheduleClose);

  /* ── Per-trigger events ───────────────────────────────────── */
  triggers.forEach(trigger => {
    const id = trigger.getAttribute('data-mega-trigger');

    // Hover → open correct panel
    trigger.addEventListener('mouseenter', () => scheduleOpen(id));

    // Click:
    //   • Panel closed  → open panel (prevent navigation)
    //   • Panel open    → navigate to the link (user confirmed intent)
    trigger.addEventListener('click', (e) => {
      if (activePanelId !== id) {
        e.preventDefault();
        openPanel(id);
        return;
      }
      const link = trigger.querySelector('.hpm-nav-btn-link');
      if (link && link.href) {
        window.location.href = link.href;
      }
    });

    // Keyboard: Enter/Space toggles panel
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (activePanelId === id) {
          closeAll();
        } else {
          openPanel(id);
          const panel = document.querySelector(`[data-mega-panel="${id}"]`);
          const firstTab = panel && panel.querySelector('.hpm-sidebar-tab');
          if (firstTab) firstTab.focus();
        }
      }
    });
  });

  /* ── Panel hover (keep open while inside panel) ─────────────
     Panel is outside the nav in the DOM hierarchy so we still
     need explicit handlers here.
  ─────────────────────────────────────────────────────────────── */
  panels.forEach(panel => {
    panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    panel.addEventListener('mouseleave', scheduleClose);
  });

  /* ── Overlay click → close ───────────────────────────────── */
  if (overlay) {
    overlay.addEventListener('click', closeAll);
  }

  /* ── Click outside → close ──────────────────────────────── */
  document.addEventListener('click', (e) => {
    if (activePanelId === null) return;
    const insideNav   = nav && nav.contains(e.target);
    const insidePanel = [...panels].some(p => p.contains(e.target));
    if (!insideNav && !insidePanel) closeAll();
  });

  /* ── Escape key ─────────────────────────────────────────── */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activePanelId !== null) {
      const trigger = document.querySelector(`[data-mega-trigger="${activePanelId}"]`);
      closeAll();
      if (trigger) trigger.focus();
    }
  });

  /* ── Tab switching (inside panel) ────────────────────────── */
  document.querySelectorAll('.hpm-sidebar-tab').forEach(tab => {
    function activateTab() {
      const tabId   = tab.getAttribute('data-tab');
      const panelEl = tab.closest('[data-mega-panel]'); // Element, not ID

      if (!tabId || !panelEl) return;

      // Deactivate all sibling tabs
      panelEl.querySelectorAll('.hpm-sidebar-tab').forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });

      // Hide all sibling content panels
      panelEl.querySelectorAll('.hpm-tab-panel').forEach(p => {
        p.classList.remove('is-active');
        p.setAttribute('hidden', '');
      });

      // Activate this tab
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      // Activate matching content panel
      const target = document.getElementById(`hpm-tab-content-${tabId}`);
      if (target) {
        target.classList.add('is-active');
        target.removeAttribute('hidden');
      }
    }

    // Hover → instant switch
    tab.addEventListener('mouseenter', activateTab);
    // Click → also switch (accessibility)
    tab.addEventListener('click', activateTab);

    // Arrow key nav within sidebar
    tab.addEventListener('keydown', (e) => {
      const panelEl = tab.closest('[data-mega-panel]');
      if (!panelEl) return;
      const allTabs = [...panelEl.querySelectorAll('.hpm-sidebar-tab')];
      const idx     = allTabs.indexOf(tab);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = allTabs[idx + 1] || allTabs[0];
        next.focus();
        next.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false }));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = allTabs[idx - 1] || allTabs[allTabs.length - 1];
        prev.focus();
        prev.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false }));
      }
    });
  });

  /* ── Re-position panels on scroll/resize ─────────────────── */
  let rAF = null;
  function reposition() {
    if (activePanelId === null) return;
    const panel = document.querySelector(`[data-mega-panel="${activePanelId}"]`);
    if (panel && !panel.hasAttribute('hidden')) setPanelTop(panel);
  }

  window.addEventListener('scroll', () => {
    if (rAF) cancelAnimationFrame(rAF);
    rAF = requestAnimationFrame(reposition);
  }, { passive: true });

  window.addEventListener('resize', () => {
    if (activePanelId !== null) closeAll();
  });

  /* ── Expose global API ─────────────────────────────────────── */
  window.HPMMegaMenu = {
    open  : openPanel,
    close : closeAll
  };

})();
